import { AxiosError, CanceledError, type AxiosRequestConfig } from "axios";
import {
  BadRequestError,
  InternalServerError,
  NotFoundError,
  TooManyRequestsError,
} from "./Errors";
import axiosInstance from "./axiosInstance";

type DomainErrorConfig = Record<string, new () => Error>;

type Config = AxiosRequestConfig & { domainErrors?: DomainErrorConfig };

export class ApiBase {
  async #wrapWithTryCatch<ResponseType>(
    promise: Promise<ResponseType>,
    domainErrors?: DomainErrorConfig,
  ) {
    try {
      return await promise;
    } catch (error) {
      if (error instanceof CanceledError) {
        throw error;
      }

      console.error(error);

      if (error instanceof AxiosError) {
        switch (error.response?.status) {
          case 422: {
            for (const domainError of error.response?.data.errors ?? []) {
              const DomainError = domainErrors?.[domainError.code];
              if (DomainError) {
                throw new DomainError();
              }
            }

            throw new InternalServerError();
          }
          case 404:
            throw new NotFoundError();
          case 400:
            throw new BadRequestError();
          case 429:
            throw new TooManyRequestsError();
          default:
            throw new InternalServerError();
        }
      }

      throw new InternalServerError();
    }
  }

  protected async get<ResponseType>(
    url: string,
    options?: Config,
  ): Promise<ResponseType> {
    return this.#wrapWithTryCatch(
      axiosInstance.get<ResponseType>(url, options).then((res) => res.data),
      options?.domainErrors,
    );
  }

  protected async post<RequestType, ResponseType>(
    url: string,
    data: RequestType,
    options?: Config,
  ): Promise<ResponseType> {
    return this.#wrapWithTryCatch(
      axiosInstance
        .post<ResponseType>(url, data, options)
        .then((res) => res.data),
      options?.domainErrors,
    );
  }

  protected async put<RequestType, ResponseType>(
    url: string,
    data: RequestType,
    options?: Config,
  ): Promise<ResponseType> {
    return this.#wrapWithTryCatch(
      axiosInstance
        .put<ResponseType>(url, data, options)
        .then((res) => res.data),
      options?.domainErrors,
    );
  }
}
